import * as React from 'react';

import {ChatPreview} from 'shells/chat/chat-preview';
import {colors} from 'shells/colors/colors';
import {PageContentPreviewable} from 'shells/layout/page-content-previewable/page-content-previewable';
import {Stack} from 'shells/layout/stack/stack';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {LoadingText} from 'shells/loading-placeholder/loading-text';

import type {ChatbotResponseTimingEnum, ChatbotToneEnum} from 'nutshell-graphql-types';
import type {ChatSettingsOfflineFormTypeEnum as OfflineFormTypeEnum} from 'nutshell-graphql-types';

import {useGetBehaviorSettings} from '../../graphql/hooks/use-get-behavior-settings';

import {ChatbotReportCard} from '../chatbot-report-card';
import {FORM_TYPE_CONVERSATIONAL, FORM_TYPE_NONE} from '../consts';
import {getChatbotIcon, getChatWidgetIcon} from '../helpers';
import {OfflinePreview} from '../offline-options/offline-preview';
import {SettingsContentWrapper} from '../settings-content-wrapper';

import {ChatBehaviorOptionsContent} from './chat-behavior-options-content';

export const OFFLINE_RULES_SECTION_ID = 'offline-rules';

export function ChatBehaviorOptions() {
    const [previewedSectionId, setPreviewedSectionId] = React.useState<string | null | undefined>(
        null
    );
    const [responseTiming, setResponseTiming] = React.useState<ChatbotResponseTimingEnum>('NEVER');
    const [tone, setTone] = React.useState<ChatbotToneEnum>('NEUTRAL');
    const [regularMessage, setRegularMessage] = React.useState<string>(
        'Hello, how can I help you today?'
    );
    const [conversationalMessage, setConversationalMessage] = React.useState<string>(
        'Hello, how can I help you today?'
    );
    const [formType, setFormType] = React.useState<OfflineFormTypeEnum>('NONE');

    const {chatSettings, refetch: refetchSettings} = useGetBehaviorSettings();

    // Set chat settings values after first load
    const hasLoadedChatSettings = React.useRef(false);
    React.useEffect(() => {
        if (chatSettings && !hasLoadedChatSettings.current) {
            hasLoadedChatSettings.current = true;
            setResponseTiming(chatSettings.chatbot.responseTiming);
            setTone(chatSettings.chatbot.tone);
            setRegularMessage(chatSettings.offline.message);
            setConversationalMessage(chatSettings.offline.conversationalMessage);
            setFormType(chatSettings.offline.formType);
        }
    }, [chatSettings]);

    const isConversational = formType === FORM_TYPE_CONVERSATIONAL;
    const currentMessage = isConversational ? conversationalMessage : regularMessage;
    const setCurrentMessage = isConversational ? setConversationalMessage : setRegularMessage;
    const isOfflineFormEnabled = formType !== FORM_TYPE_NONE;

    const getPreview = () => {
        if (!chatSettings) {
            return (
                <div className='flex justify-center align-center full-width full-height'>
                    <LoadingText height={500} width={305} color={colors.plumLt} />
                </div>
            );
        }

        if (responseTiming === 'NEVER' || previewedSectionId === OFFLINE_RULES_SECTION_ID) {
            return (
                <OfflinePreview
                    isOfflineFormEnabled={isOfflineFormEnabled}
                    message={currentMessage}
                    lookAndFeel={chatSettings.lookAndFeel}
                    isConversational={isConversational}
                />
            );
        }

        return (
            <ChatPreview
                headerTitle={chatSettings.lookAndFeel.nameDisplay}
                icon={getChatWidgetIcon(
                    chatSettings.lookAndFeel.icon.type,
                    chatSettings.lookAndFeel.icon.url
                )}
                backgroundColor={chatSettings.lookAndFeel.color}
                status='chatbot'
                name={chatSettings.chatbot.name}
                tone={tone}
                chatbotIcon={getChatbotIcon(chatSettings.chatbot.iconEnum)}
                showPoweredBy={chatSettings.lookAndFeel.showPoweredBy}
                showDisclaimer={chatSettings.lookAndFeel.disclaimerSettings.isEnabled}
                disclaimerText={chatSettings.lookAndFeel.disclaimerSettings.message}
            />
        );
    };

    return (
        <SettingsContentWrapper>
            <PageContentPreviewable
                getPreview={getPreview}
                leftContent={
                    chatSettings ? (
                        <Stack spacing={32}>
                            <ChatBehaviorOptionsContent
                                chatSettings={chatSettings}
                                responseTiming={responseTiming}
                                setResponseTiming={setResponseTiming}
                                tone={tone}
                                setTone={setTone}
                                hasNoContent={false}
                                refetchSettings={refetchSettings}
                                formType={formType}
                                setFormType={setFormType}
                                message={currentMessage}
                                setMessage={setCurrentMessage}
                                setPreviewedSectionId={(sectionIdInput?: string | null) => {
                                    setTimeout(() => {
                                        setPreviewedSectionId(sectionIdInput);
                                    }, 200);
                                }}
                            />
                            {NutClientConfig.hasChatbotReportFF && <ChatbotReportCard />}
                        </Stack>
                    ) : (
                        <LoadingPage size={60} />
                    )
                }
            />
        </SettingsContentWrapper>
    );
}
